// One requirement in the install dialog's *What it needs* pane (keepr-api
// KPR-1811; plan v4 §5.12): the bundle's card, Use a card you have or Add a
// new one, the person's card it uses, and the match list. Choosing is the
// approval (D7): a row that would change the person's card starts unset.
import React from 'react';
import { Input, Segmented, Select } from 'antd';

import RecordBadge from '../common/RecordBadge';
import { getDataTypeMeta } from '../../utils/dictionary';
import { candidateOf, fitTags, isAsked, isSettled, listsOf, startingCardChoice } from '../../utils/packageBindings';

const ADD = 'add';
const LEAVE = 'leave';
const CHOICE_TYPES = new Set(['choice']);

const typeWords = row => `${getDataTypeMeta(row.dataType || 'text-small')?.label || 'Short text'}${row.calculated ? ', worked out by keepr' : ''}`;
const labelOf = el => (typeof el?.label === 'string' ? el.label : el?.label?.singular) || el?.name || '';

/** The person's element by id, from the card definitions the app holds (a parent's and a set's included). */
const elementById = (elements, id) => (elements || []).find(el => el && String(el.id || el._id) === String(id)) || null;

/** The value Select shows for an element choice. */
function elementValue(element) {
	if (!element) return undefined;
	if (element.kind === 'add') return ADD;
	return `${element.kind === 'convert' ? 'c' : 'm'}:${element.id}`;
}

function ElementRow({ row, choice, cardName, theirs, problems, onElement, onValue }) {
	const element = choice.elements?.[row.name] || null;
	const isChoice = CHOICE_TYPES.has(row.dataType);
	// A legacy text with choices standing in for a choice is converted, never read as it is (D29).
	const converts = offer => isChoice && ((row.convert && row.target === offer.id) || (theirs(offer.id) && !CHOICE_TYPES.has(theirs(offer.id).dataType)));
	const options = [
		...(row.offers || []).map(offer => ({
			value: `${converts(offer) ? 'c' : 'm'}:${offer.id}`,
			disabled: !offer.offered,
			title: offer.reason || offer.label,
			label: (
				<span className="install-needs__option">
					<span>{converts(offer) ? `${offer.label}: convert to a choice` : offer.label}</span>
					{!offer.offered && offer.reason ? <span className="install-needs__option-reason">{offer.reason}</span> : null}
				</span>
			),
		})),
		{ value: ADD, label: `Add to ${cardName}…`, title: `Add to ${cardName}` },
	];
	const pick = (value) => {
		if (value === ADD) onElement({ kind: 'add', label: row.label });
		else onElement({ kind: value.startsWith('c:') ? 'convert' : 'match', id: value.slice(2) });
	};
	const target = element && element.kind !== 'add' ? theirs(element.id) : null;
	const theirChoices = Array.isArray(target?.options?.choices) ? target.options.choices : [];
	const showValues = isChoice && Array.isArray(row.values) && element && element.kind !== 'add';
	return (
		<li className="install-needs__row">
			<div className="install-needs__bundle">
				<span className="install-needs__label">{row.label}</span>
				<span className="install-needs__type">{typeWords(row)}{isChoice && Array.isArray(row.values) ? ` · ${row.values.length === 1 ? '1 value' : `${row.values.length} values`}` : ''}</span>
			</div>
			<div className="install-needs__theirs">
				<Select
					value={elementValue(element)}
					onChange={pick}
					options={options}
					placeholder="Choose an element"
					aria-label={`What ${row.label} uses`}
					status={problems.length ? 'error' : undefined}
					style={{ width: '100%' }}
				/>
				{element && element.kind === 'add' ? (
					<Input
						value={element.label}
						onChange={e => onElement({ kind: 'add', label: e.target.value })}
						maxLength={100}
						aria-label={`Name of the element added to ${cardName}`}
						className="install-needs__add-label"
					/>
				) : null}
				{problems.map(p => <p key={p} className="install-needs__problem">{p}</p>)}
			</div>
			{showValues ? (
				<ul className="install-needs__values" aria-label={`The values of ${row.label}`}>
					{row.values.map((v) => {
						const value = choice.values?.[row.name]?.[v.value] || null;
						return (
							<li key={v.value} className="install-needs__row install-needs__row--value">
								<span className="install-needs__label">{v.label || v.value}</span>
								<Select
									value={value ? (value.kind === 'add' ? ADD : `v:${value.value}`) : undefined}
									onChange={next => onValue(v.value, next === ADD ? { kind: 'add' } : { kind: 'match', value: next.slice(2) })}
									options={[
										...theirChoices.map(c => ({ value: `v:${c.value}`, label: c.label || c.value })),
										...(v.target && !theirChoices.some(c => c.value === v.target) ? [{ value: `v:${v.target}`, label: v.target }] : []),
										{ value: ADD, label: 'Add to their list' },
									]}
									placeholder="Choose a value"
									aria-label={`What ${v.label || v.value} uses`}
									style={{ width: '100%' }}
								/>
							</li>
						);
					})}
				</ul>
			) : null}
		</li>
	);
}

/** A row the record settled earlier (an add-on added later, KPR-1812): read, never moved. */
function SettledRow({ row, theirs }) {
	const words = row.gone ? 'Its element was deleted from your card' : (row.hidden ? 'Matched' : `Matched to ${labelOf(theirs(row.target)) || 'your element'}`);
	return (
		<li className="install-needs__row install-needs__row--settled">
			<div className="install-needs__bundle">
				<span className="install-needs__label">{row.label}</span>
				<span className="install-needs__type">{typeWords(row)}</span>
			</div>
			<span className="meta-line">{words}</span>
		</li>
	);
}

export default function InstallNeedSection({ requirement, choice: chosen, onChange, problems = {}, elementsOf }) {
	const name = requirement.card?.name || 'its card';
	const candidates = Array.isArray(requirement.candidates) ? requirement.candidates : [];
	const provided = Boolean(requirement.provided);
	// No choice: a card another package brings, which holds until the person picks one of theirs (KPR-1829).
	const choice = chosen || { use: 'card', cardId: null, elements: {}, values: {}, lists: {} };
	// Bound here already (an add-on later): the card stays; only what is new is asked.
	const settled = isSettled(requirement);
	const candidate = choice.use === 'card' ? candidateOf(requirement, choice.cardId) : null;
	const cardName = candidate?.name || 'your card';
	const theirElements = candidate ? elementsOf(candidate.cardId) : [];
	const theirs = id => elementById(theirElements, id);
	const rows = candidate ? (candidate.rows || []) : [];
	const asked = rows.filter(isAsked);
	const lists = listsOf(candidate);
	const contract = requirement.contract;

	const setUse = (use) => {
		if (use === 'own') onChange({ use: 'own', cardId: null, elements: {}, values: {}, lists: {} });
		else onChange(startingCardChoice(candidateOf(requirement, choice.cardId) || candidates[0]));
	};
	const setElement = (rowName, element) => {
		const was = choice.elements?.[rowName];
		const sameTarget = was && element && was.kind !== 'add' && element.kind !== 'add' && was.id === element.id;
		// A different element of theirs means its values are asked again.
		const values = sameTarget ? choice.values : { ...choice.values, [rowName]: Object.fromEntries(Object.keys(choice.values?.[rowName] || {}).map(k => [k, null])) };
		onChange({ ...choice, elements: { ...choice.elements, [rowName]: element }, values });
	};
	const setValue = (rowName, value, next) => onChange({ ...choice, values: { ...choice.values, [rowName]: { ...(choice.values?.[rowName] || {}), [value]: next } } });

	return (
		<section className="install-needs__need" aria-label={`What ${name} uses`}>
			<div className="install-needs__head">
				<RecordBadge kind="card" record={requirement.card} title={name} size="sm" />
				<span className="meta-line">{`needs ${contract === 1 ? '1 element' : `${contract} elements`}`}</span>
			</div>
			{provided || settled ? null : (
				<Segmented
					value={choice.use}
					onChange={setUse}
					options={[
						{ value: 'card', label: 'Use a card you have', disabled: !candidates.length },
						{ value: 'own', label: `Add a new ${name}` },
					]}
					aria-label={`What ${name} uses`}
					className="install-needs__use"
				/>
			)}
			{!chosen && provided ? (
				<p className="meta-line install-needs__line">{`Uses the ${name} card ${requirement.provided?.package ? `that ${requirement.provided.package} brings` : 'another package brings'}. Choose one of your cards instead, if you keep these already.`}</p>
			) : null}
			{problems[''] && choice.use === 'own' ? problems[''].map(p => <p key={p} className="install-needs__problem">{p}</p>) : null}
			{choice.use === 'own' ? (
				<p className="meta-line install-needs__line">
					{requirement.own?.note || (requirement.own && requirement.own.keyFree === false
						? `Your collection already has a card of this kind. This one is added beside it under a name set apart; rename it to something clearer.`
						: `Adds its own ${name} card.`)}
				</p>
			) : (
				<>
					<Select
						value={choice.cardId || undefined}
						onChange={cardId => onChange(startingCardChoice(candidateOf(requirement, cardId)))}
						placeholder={candidates.length ? 'Choose a card' : 'No card here fits'}
						disabled={!candidates.length}
						aria-label={`Your card for ${name}`}
						className="install-needs__card"
						style={{ width: '100%' }}
						// The field shows the card's badge alone; its tags sit on the line beneath, so they never crowd it.
						labelRender={({ value }) => {
							const c = candidateOf(requirement, value);
							return c ? <RecordBadge kind="card" record={c} title={c.name} size="xs" /> : value;
						}}
						options={candidates.map(c => ({
							value: c.cardId,
							title: c.name,
							label: (
								<span className="install-needs__candidate">
									<RecordBadge kind="card" record={c} title={c.name} size="xs" />
									{fitTags(requirement, c).map(tag => <span key={tag} className="install-needs__tag">{tag}</span>)}
								</span>
							),
						}))}
					/>
					{candidate ? <p className="install-needs__tags">{fitTags(requirement, candidate).map(tag => <span key={tag} className="install-needs__tag">{tag}</span>)}</p> : null}
					{candidate && Array.isArray(candidate.problems) && candidate.problems.length ? (
						<ul className="install-needs__problems">
							{candidate.problems.map(p => <li key={`${p.element || ''}:${p.reason}`} className="install-needs__problem">{p.reason}</li>)}
						</ul>
					) : null}
					{problems[''] ? problems[''].map(p => <p key={p} className="install-needs__problem">{p}</p>) : null}
					{candidate && Array.isArray(candidate.outsideList) && candidate.outsideList.length ? (
						<p className="meta-line install-needs__line">
							{candidate.outsideList.map(o => `${o.items === 1 ? '1 item holds' : `${o.items} items hold`} a value outside the list of ${labelOf(theirs(o.element)) || o.element}`).join('; ')}
							{'. Converting adds those values to the list.'}
						</p>
					) : null}
					{rows.length ? (
						<>
							<h4 className="micro-label install-needs__sub">Match its elements</h4>
							<ul className="install-needs__rows">
								{rows.map(row => (isAsked(row)
									? <ElementRow key={row.name} row={row} choice={choice} cardName={cardName} theirs={theirs} problems={problems[row.name] || []} onElement={el => setElement(row.name, el)} onValue={(value, next) => setValue(row.name, value, next)} />
									: (row.waits ? null : <SettledRow key={row.name} row={row} theirs={theirs} />)))}
							</ul>
						</>
					) : null}
					{lists.length ? (
						<>
							<h4 className="micro-label install-needs__sub">Lists it would put on your card</h4>
							<ul className="install-needs__rows">
								{lists.map(listName => (
									<li key={listName} className="install-needs__row">
										<div className="install-needs__bundle">
											<span className="install-needs__label">{listName}</span>
											<span className="install-needs__type">Related list</span>
										</div>
										<Select
											value={choice.lists?.[listName] || undefined}
											onChange={next => onChange({ ...choice, lists: { ...choice.lists, [listName]: next } })}
											options={[{ value: ADD, label: 'Add it' }, { value: LEAVE, label: 'Leave it off' }]}
											placeholder="Choose"
											aria-label={`The ${listName} list on ${cardName}`}
											style={{ width: '100%' }}
										/>
									</li>
								))}
							</ul>
						</>
					) : null}
					{asked.length === 0 && !lists.length && candidate ? <p className="meta-line install-needs__line">Nothing to match: it uses your card as it is.</p> : null}
				</>
			)}
		</section>
	);
}
